import { Link } from 'react-router';
import { addDays, endOfDay, format, isPast, isToday } from 'date-fns';
import { AlertTriangle, CalendarClock, CalendarDays, type LucideIcon } from 'lucide-react';
import { type ChangeManagement } from '@/hooks/useChangeManagements';
import { CM_RUNNING_STATES, CM_SCHEDULED_STATES, CM_STATE_DOT, cmStateLabel } from '@/lib/cmState';
import { cn } from '@/lib/utils';

interface CMAgendaProps {
  cms: ChangeManagement[];
}

interface Section {
  key: string;
  label: string;
  icon: LucideIcon;
  accent: string;
  items: ChangeManagement[];
}

function parse(t: string | null): Date | null {
  if (!t) return null;
  const d = new Date(t);
  return Number.isNaN(d.getTime()) ? null : d;
}

/**
 * Time-aware schedule strip ("today's work"): what's running over / missed its
 * window, what starts today, and what's coming this week — driven by each
 * change's start_time / expected_end_time. Complements CMDashboard (which buckets
 * by personal action state, not time). Renders nothing when there's no schedule.
 */
export default function CMAgenda({ cms }: CMAgendaProps) {
  const now = new Date();
  const todayEnd = endOfDay(now);
  const weekEnd = endOfDay(addDays(now, 7));

  const overdue = cms.filter((c) => {
    if (c.state === 'completed' || c.state === 'cancelled') return false;
    if (c.autoStartMissedAt) return true; // missed its scheduled auto-start
    const end = parse(c.expectedEndTime);
    return CM_RUNNING_STATES.includes(c.state) && end != null && isPast(end); // running over
  });
  const overdueIds = new Set(overdue.map((c) => c.id));

  const startingToday = cms.filter((c) => {
    if (overdueIds.has(c.id)) return false;
    const start = parse(c.startTime);
    return CM_SCHEDULED_STATES.includes(c.state) && start != null && isToday(start);
  });
  const startingTodayIds = new Set(startingToday.map((c) => c.id));

  const thisWeek = cms.filter((c) => {
    if (overdueIds.has(c.id) || startingTodayIds.has(c.id)) return false;
    const start = parse(c.startTime);
    return (
      CM_SCHEDULED_STATES.includes(c.state) &&
      start != null &&
      start > todayEnd &&
      start <= weekEnd
    );
  });

  const byStart = (a: ChangeManagement, b: ChangeManagement) =>
    (parse(a.startTime)?.getTime() ?? 0) - (parse(b.startTime)?.getTime() ?? 0);

  const sections: Section[] = [
    { key: 'overdue', label: 'Running over / missed window', icon: AlertTriangle, accent: 'text-sev-1', items: [...overdue].sort(byStart) },
    { key: 'today', label: 'Starting today', icon: CalendarClock, accent: 'text-sev-5', items: [...startingToday].sort(byStart) },
    { key: 'week', label: 'This week', icon: CalendarDays, accent: 'text-muted-foreground', items: [...thisWeek].sort(byStart) },
  ].filter((s) => s.items.length > 0);

  if (sections.length === 0) return null;

  return (
    <section className="mb-5">
      <h2 className="text-sm font-semibold mb-3">Schedule</h2>
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3">
        {sections.map((s) => {
          const Icon = s.icon;
          return (
            <div key={s.key} className="bg-card border border-border rounded-lg p-4">
              <div className="flex items-center gap-2 mb-2">
                <Icon className={cn('w-4 h-4', s.accent)} aria-hidden="true" />
                <span className="text-sm font-medium">{s.label}</span>
                <span className="ml-auto text-sm tabular-nums text-muted-foreground">{s.items.length}</span>
              </div>
              <ul className="space-y-1.5">
                {s.items.slice(0, 5).map((cm) => {
                  const start = parse(cm.startTime);
                  return (
                    <li key={cm.id} className="flex items-center gap-2 min-w-0">
                      <span
                        className={cn('w-1.5 h-1.5 rounded-full shrink-0', CM_STATE_DOT[cm.state])}
                        title={cmStateLabel(cm.state)}
                        aria-hidden="true"
                      />
                      <Link to={`/change-management/${cm.id}`} className="text-sm truncate hover:underline">
                        {cm.title}
                      </Link>
                      {start ? (
                        <span className="ml-auto text-xs text-muted-foreground tabular-nums shrink-0">
                          {s.key === 'today' ? format(start, 'HH:mm') : format(start, 'EEE HH:mm')}
                        </span>
                      ) : null}
                    </li>
                  );
                })}
                {s.items.length > 5 ? (
                  <li className="text-xs text-muted-foreground">+{s.items.length - 5} more</li>
                ) : null}
              </ul>
            </div>
          );
        })}
      </div>
    </section>
  );
}
